/**
 * Operational notices derived only from project summaries already loaded by
 * DashboardPage. This panel does not infer soil conditions or agronomic risk.
 */
import {
	Alert,
	Box,
	Chip,
	Divider,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ProjectStatus, type ProjectSummaryDTO } from "@flaha/shared-types";

import { GlassSurface } from "../../../layouts/components/GlassSurface";

export interface AlertsPanelProps {
	projects: ProjectSummaryDTO[] | null;
	error: string | null;
}

interface DashboardNotice {
	severity: "info" | "warning";
	severityLabel: "Information" | "Attention";
	title: string;
	detail: string;
}

function computeNotices(projects: ProjectSummaryDTO[]): DashboardNotice[] {
	const notices: DashboardNotice[] = [];
	const archived = projects.filter((project) => project.status === ProjectStatus.ARCHIVED);
	const emptyActive = projects.filter(
		(project) => project.status === ProjectStatus.ACTIVE && project.sampleCount === 0
	);

	if (emptyActive.length > 0) {
		notices.push({
			severity: "warning",
			severityLabel: "Attention",
			title: `${emptyActive.length} active project${emptyActive.length === 1 ? "" : "s"} without samples`,
			detail: "This notice is derived from ACTIVE status and the API-supplied zero sample count.",
		});
	}
	if (archived.length > 0) {
		notices.push({
			severity: "info",
			severityLabel: "Information",
			title: `${archived.length} archived project${archived.length === 1 ? "" : "s"}`,
			detail: "Archived projects remain available as historical project context.",
		});
	}

	return notices;
}

export function AlertsPanel({ projects, error }: AlertsPanelProps) {
	const notices = projects ? computeNotices(projects) : [];

	return (
		<GlassSurface
			component="section"
			aria-labelledby="project-notices-title"
			intensity="standard"
			depth="raised"
		>
			<Box sx={{ p: 2.5 }}>
				<Typography id="project-notices-title" component="h2" variant="h6">
					Project notices
				</Typography>
				<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
					Operational signals derived only from loaded project summaries
				</Typography>
			</Box>
			<Divider sx={{ borderColor: "workspace.border" }} />
			<Box sx={{ p: 2.5 }} aria-busy={projects === null && !error}>
				{error ? (
					<Alert severity="warning">
						Project notices are unknown because project data is unavailable.
					</Alert>
				) : projects === null ? (
					<Stack spacing={1} aria-label="Loading project notices">
						<Skeleton variant="rounded" height={54} sx={{ bgcolor: "rgba(246,240,227,0.09)" }} />
					</Stack>
				) : notices.length === 0 ? (
					<Stack direction="row" spacing={1.5} alignItems="flex-start">
						<CheckCircleOutlineIcon color="success" aria-hidden="true" />
						<Box>
							<Typography variant="subtitle2">No project-derived notices</Typography>
							<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
								The loaded project summaries contain no archived projects and no active
								projects with a zero sample count. Soil-test warnings remain inside their
								authoritative test and report views.
							</Typography>
						</Box>
					</Stack>
				) : (
					<Stack spacing={1.5}>
						{notices.map((notice) => (
							<Alert
								key={notice.title}
								severity={notice.severity}
								variant="outlined"
								action={<Chip label={notice.severityLabel} size="small" />}
							>
								<Typography variant="subtitle2">{notice.title}</Typography>
								<Typography variant="caption">{notice.detail}</Typography>
							</Alert>
						))}
					</Stack>
				)}
			</Box>
		</GlassSurface>
	);
}
